'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { jetbrainsMono } from 'utils/fonts';
import {
  SharedDetailModal,
  SharedDetailModalSection,
} from './SharedDetailModal';
import styles from './DiagnosticLearnMoreModal.module.css';

export interface ModalSection {
  title?: string;
  content: string;
  subtitle?: string;
  defaultOpen?: boolean;
}

export interface LearnMoreModalData {
  thumbnailSrc?: string;
  sectionImageSrc?: string;
  sections?: ModalSection[];
  description?: string;
  benefits?: string[];
  ctaText: string;
}

interface DiagnosticLearnMoreModalProps {
  title: string;
  modalData: LearnMoreModalData;
  onClose: () => void;
  onCtaClick?: () => void;
}

export const DiagnosticLearnMoreModal: React.FC<
  DiagnosticLearnMoreModalProps
> = ({ title, modalData, onClose, onCtaClick }) => {
  const t = useTranslations('performance-lab');
  const tPerformanceLabs = useTranslations('diagnostics');
  const { sections = [], ctaText, thumbnailSrc, sectionImageSrc, description, benefits } = modalData;
  // A missing key falls back to "namespace.key" (i18n/request.ts), which is
  // not the input — so a !== check treats plain titles like "Cryotherapy" as hits.
  const resolveText = (value: string): string => {
    if (tPerformanceLabs.has(value)) return tPerformanceLabs(value);
    if (t.has(value)) return t(value);
    return value;
  };
  const modalSections: SharedDetailModalSection[] = sections.map((section) => ({
    title: section.title,
    content: section.content,
  }));

  return (
    <SharedDetailModal
      title={title}
      iconSrc={thumbnailSrc}
      iconAlt=""
      sections={modalSections}
      sectionImageSrc={sectionImageSrc}
      description={description}
      benefits={benefits}
      onClose={onClose}
      closeAriaLabel={t('modal.closeAria')}
      resolveText={resolveText}
      footer={
        <button
          type="button"
          onClick={onCtaClick}
          className={`${styles.modalFooterCta} ${jetbrainsMono.className}`}
        >
          {ctaText}
        </button>
      }
    />
  );
};
